import React, { useState } from 'react';
import axios from 'axios';
import { Search, X, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UnsplashImagePicker({ isOpen, onClose, onSelect }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rateLimit, setRateLimit] = useState({ remaining: '?', total: '?' });

  const searchImages = async (query, page = 1) => {
    if (!query.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/unsplash/search`, {
        params: {
          query: query,
          page: page,
          per_page: 12
        }
      });

      setImages(response.data.results || []);
      setTotalPages(response.data.total_pages || 0);
      setCurrentPage(page);
      setRateLimit(response.data.rate_limit || { remaining: '?', total: '?' });
      
      if (response.data.results.length === 0) {
        toast.info('No images found. Try different search terms.');
      }
    } catch (error) {
      console.error('Failed to search images:', error);
      if (error.response?.status === 429) {
        toast.error('Rate limit exceeded. Please try again in a few minutes.');
      } else {
        toast.error('Failed to search images. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchImages(searchQuery, 1);
  };

  const handleSelectImage = async (photo) => {
    try {
      // Track download as required by Unsplash API
      if (photo.links?.download_location) {
        await axios.post(`${API}/unsplash/track-download`, null, {
          params: { download_location: photo.links.download_location }
        });
      }

      // Prepare image data
      const imageData = {
        url: photo.urls.regular,
        fullUrl: photo.urls.full,
        thumbnailUrl: photo.urls.small,
        photographer: photo.user.name,
        photographerUsername: photo.user.username,
        photographerUrl: photo.user.portfolio_url || `https://unsplash.com/@${photo.user.username}`,
        unsplashPhotoId: photo.id,
        description: photo.description || photo.alt_description || 'Unsplash photo',
        width: photo.width,
        height: photo.height
      };

      onSelect(imageData);
      toast.success('Image selected!');
      onClose();
    } catch (error) {
      console.error('Error selecting image:', error);
      toast.error('Failed to select image');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      searchImages(searchQuery, newPage);
      // Scroll to top of modal
      document.querySelector('.image-grid-container')?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Search Images</h2>
            <p className="text-sm text-gray-600 mt-1">
              Powered by Unsplash • Rate limit: {rateLimit.remaining}/{rateLimit.total} requests
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search Form */}
        <div className="p-6 border-b border-gray-200">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for images (e.g., 'mountain landscape', 'acupuncture')"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              disabled={loading || !searchQuery.trim()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors font-medium"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </form>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-auto p-6 image-grid-container">
          {loading && (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {!loading && images.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((photo) => (
                <div
                  key={photo.id}
                  className="relative group overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow bg-gray-100"
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={photo.urls.small}
                      alt={photo.alt_description || 'Unsplash photo'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>

                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300 flex flex-col justify-end p-4 opacity-0 group-hover:opacity-100">
                    <div className="text-white text-xs mb-2">
                      <p className="font-semibold">Photo by {photo.user.name}</p>
                      {photo.description && (
                        <p className="line-clamp-2 mt-1">{photo.description}</p>
                      )}
                    </div>

                    <button
                      onClick={() => handleSelectImage(photo)}
                      className="w-full px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Select Image
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && images.length === 0 && searchQuery && (
            <div className="text-center text-gray-500 py-12">
              <p className="text-lg">No images found for "{searchQuery}"</p>
              <p className="text-sm mt-2">Try different search terms</p>
            </div>
          )}

          {!loading && images.length === 0 && !searchQuery && (
            <div className="text-center text-gray-500 py-12">
              <Search className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Enter a search term to find images</p>
              <p className="text-sm mt-2">High-quality, royalty-free photos from Unsplash</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <div className="p-6 border-t border-gray-200 flex items-center justify-center gap-4">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            <span className="text-gray-600 font-medium">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}

        {/* Attribution Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 text-center text-xs text-gray-600">
          Images powered by{' '}
          <a
            href="https://unsplash.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline inline-flex items-center gap-1"
          >
            Unsplash
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
